import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Search } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";

import { advisorTopicsApi } from "../api/advisorTopicsApi";
import { topicTitleOf, formatTopicDate } from "../model/topicFormat";
import { MarkdownText } from "./MarkdownText";
import type { TopicThreadMessage } from "../model/topicTypes";

const PAGE_SIZE = 10;

/**
 * 军师历史时间线(C2,只读)。列本人已归档话题(可搜标题/摘要)→ 点开只读整段。
 * 记忆为脊:历史只回看,不改任何持久数据。`onBack` 回到当前对话。
 */
export function TopicHistory({
  onBack,
  onContinue,
}: {
  onBack: () => void;
  /** #28/#35:从这条历史接着聊。由面板实现(它才拿得到会话与判据)。 */
  onContinue?: (topicId: string) => Promise<void> | void;
}) {
  const [draft, setDraft] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["advisor", "topics", { query, page }],
    queryFn: () => advisorTopicsApi.listTopics({ query, page, size: PAGE_SIZE }),
  });

  // 只读整段:独立子组件(自持 thread query),不与列表 hook 顺序纠缠。
  if (selected) {
    return (
      <TopicThreadView
        topicId={selected}
        onBack={() => setSelected(null)}
        onContinue={onContinue}
      />
    );
  }

  const applySearch = () => {
    setQuery(draft.trim());
    setPage(1);
  };

  const total = list.data?.total ?? 0;
  const records = list.data?.records ?? [];
  const hasPrev = page > 1;
  const hasNext = page * PAGE_SIZE < total;

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex shrink-0 items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 gap-1 px-2 text-xs text-muted-foreground"
          onClick={onBack}
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          返回对话
        </Button>
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applySearch()}
            placeholder="搜索历史话题(标题/摘要）…"
            className="h-8 pl-8 text-sm"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto rounded-md border p-2">
        {list.isLoading && <div className="p-4 text-sm text-muted-foreground">正在翻历史…</div>}
        {list.isError && (
          <div className="p-4 text-sm text-destructive">历史加载失败,稍后再试。</div>
        )}
        {list.data && records.length === 0 && (
          <div className="p-4 text-sm text-muted-foreground">
            {query ? "没有匹配的历史话题。" : "还没有历史话题。开新话题聊几句,归档后会出现在这里。"}
          </div>
        )}
        <ul className="space-y-1.5">
          {records.map((t) => (
            <li key={t.topicId}>
              <button
                type="button"
                onClick={() => setSelected(t.topicId)}
                className="w-full rounded-md border border-transparent px-3 py-2 text-left transition-colors hover:border-border hover:bg-muted"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-sm font-medium">{topicTitleOf(t)}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {formatTopicDate(t.lastAt)}
                  </span>
                </div>
                {t.summary && (
                  <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                    {t.summary}
                  </div>
                )}
                <div className="mt-1 text-2xs text-muted-foreground">{t.messageCount} 条</div>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {(hasPrev || hasNext) && (
        <div className="flex shrink-0 items-center justify-between text-xs text-muted-foreground">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            disabled={!hasPrev}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            上一页
          </Button>
          <span>第 {page} 页</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            disabled={!hasNext}
            onClick={() => setPage((p) => p + 1)}
          >
            下一页
          </Button>
        </div>
      )}
    </div>
  );
}

/** 只读整段视图:解密后的历史对话(assistant 走 markdown,user 纯文本)。 */
function TopicThreadView({
  topicId,
  onBack,
  onContinue,
}: {
  topicId: string;
  onBack: () => void;
  onContinue?: (topicId: string) => Promise<void> | void;
}) {
  const [switching, setSwitching] = useState(false);
  const thread = useQuery({
    queryKey: ["advisor", "topic-thread", topicId],
    queryFn: () => advisorTopicsApi.topicThread(topicId),
  });

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex shrink-0 items-center">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 gap-1 px-2 text-xs text-muted-foreground"
          onClick={onBack}
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          返回列表
        </Button>
        {/* #28/#35:只读回看之外,给一条「接着聊」的路。切没切成由面板按
            topicId === requestedTopicId 判,不看有没有报错。 */}
        {onContinue && (
          <Button
            type="button"
            size="sm"
            className="ml-auto h-8 px-2 text-xs"
            disabled={switching}
            onClick={async () => {
              setSwitching(true);
              try {
                await onContinue(topicId);
              } finally {
                setSwitching(false);
              }
            }}
          >
            {switching ? "接过来中…" : "从这条继续"}
          </Button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto rounded-md border p-4">
        {thread.isLoading && <div className="text-sm text-muted-foreground">正在打开…</div>}
        {thread.isError && (
          <div className="text-sm text-destructive">这段历史打不开,稍后再试。</div>
        )}
        {(thread.data ?? []).map((m, i) => (
          <ReadonlyBubble key={i} message={m} />
        ))}
      </div>
    </div>
  );
}

function ReadonlyBubble({ message }: { message: TopicThreadMessage }) {
  const mine = message.msgRole === "user";
  return (
    <div className={`my-2 flex ${mine ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
          mine ? "whitespace-pre-wrap bg-primary text-primary-foreground" : "bg-muted"
        }`}
      >
        {mine ? message.content : <MarkdownText text={message.content} />}
      </div>
    </div>
  );
}
